import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  deleteNotification,
  deleteReadNotifications,
  getNotifications,
  getUnreadCount,
  markAllAsRead,
  markAsRead,
} from '../services/notifications';

// ─── Constants ────────────────────────────────────────────────────────────────

/** How often (ms) the context polls for new notifications while the tab is visible. */
const POLL_INTERVAL_MS = 30_000;

/** How often (ms) the context polls while the tab is hidden (reduced rate). */
const POLL_INTERVAL_HIDDEN_MS = 120_000;

// ─── Context ──────────────────────────────────────────────────────────────────

const NotificationContext = createContext(null);

/**
 * NotificationProvider
 *
 * Provides real-time-ish notification state to the entire admin app via
 * periodic polling. Polling frequency adapts to whether the browser tab is
 * currently visible to avoid unnecessary requests when the user is elsewhere.
 *
 * Exposed values:
 *   - notifications   {Array}   - Latest page of notifications (most recent first)
 *   - unreadCount     {number}  - Total unread count
 *   - loading         {boolean} - True while the initial fetch is in progress
 *   - refresh()       - Manually re-fetch notifications
 *   - handleMarkRead(id)
 *   - handleMarkAllRead()
 *   - handleDelete(id)
 *   - handleDeleteRead()
 */
export function NotificationProvider({ children }) {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(true);

  const pollTimerRef = useRef(null);

  // ─── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getNotifications({ limit: 20 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (_) {
      // Silently ignore network errors — the bell will just stay stale.
    }
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getUnreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch (_) {}
  }, [user]);

  // ─── Polling ────────────────────────────────────────────────────────────────

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);

    const interval = document.hidden ? POLL_INTERVAL_HIDDEN_MS : POLL_INTERVAL_MS;

    pollTimerRef.current = setInterval(() => {
      fetchUnreadCount();
    }, interval);
  }, [fetchUnreadCount]);

  // Restart polling when visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible — poll immediately then restart at fast rate
        fetchUnreadCount();
      }
      startPolling();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchUnreadCount, startPolling]);

  // Initial load + start polling when user logs in
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }

    setLoading(true);
    fetchNotifications().finally(() => setLoading(false));
    startPolling();

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [user, fetchNotifications, startPolling]);

  // ─── Action handlers ────────────────────────────────────────────────────────

  const refresh = useCallback(() => fetchNotifications(), [fetchNotifications]);

  const handleMarkRead = useCallback(async (id) => {
    await markAsRead(id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      )
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    await markAllAsRead();
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    setUnreadCount(0);
  }, []);

  const handleDelete = useCallback(async (id) => {
    const target = notifications.find((n) => n.id === id);
    await deleteNotification(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (target && !target.read_at) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  }, [notifications]);

  const handleDeleteRead = useCallback(async () => {
    await deleteReadNotifications();
    setNotifications((prev) => prev.filter((n) => !n.read_at));
  }, []);

  // ─── Context value ──────────────────────────────────────────────────────────

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        refresh,
        handleMarkRead,
        handleMarkAllRead,
        handleDelete,
        handleDeleteRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

/** Convenience hook — throws if used outside <NotificationProvider> */
export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used inside <NotificationProvider>');
  }
  return ctx;
}
